/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * SegmentedTabs - Reusable segmented control tab bar
 * Works with tab IDs for flexible ordering - parent can reorder tabs at will
 *
 * @file SegmentedTabs.jsx
 * @author Satyaki Bose
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, makeStyles } from '@material-ui/core';
import mixins from '../../assets/styles/mixins.scss';

const useStyles = makeStyles(() => ({
  root: {
    display: 'inline-flex',
    height: 50,
    padding: 4,
    backgroundColor: mixins.quaternary,
    borderRadius: 12,
    boxSizing: 'border-box',
    minHeight: 'unset',
    '& .MuiTabs-indicator': {
      display: 'none'
    },
    '& .MuiTabs-flexContainer': {
      gap: 0,
      height: '100%'
    }
  },
  tab: {
    height: 42,
    padding: '8px 16px',
    minWidth: 184,
    margin: 0,
    flex: '1 1 0',
    borderRadius: 8,
    minHeight: 42,
    fontWeight: mixins.fontWeight500,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    textTransform: 'none',
    color: mixins.color_text_primary,
    opacity: 1,
    whiteSpace: 'nowrap',
    '&.Mui-selected': {
      backgroundColor: mixins.chalkWhiteFont
    }
  }
}));

const SegmentedTabs = ({
  value,
  onChange,
  tabs,
  className
}) => {
  const classes = useStyles();

  // Convert ID to index for MUI Tabs (which requires numeric value)
  const selectedIndex = Math.max(0, tabs.findIndex(tab => tab.id === value));

  // Handle tab change - convert index back to ID
  const handleChange = (event, newIndex) => {
    const newTabId = tabs[newIndex]?.id;
    if (newTabId && onChange) {
      onChange(newTabId, event);
    }
  };

  return (
    <Tabs
      value={selectedIndex}
      onChange={handleChange}
      classes={{ root: `${classes.root} ${className || ''}` }}
      aria-label="segmented tabs"
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.id}
          label={tab.label}
          id={`segmented-tab-${tab.id}`}
          aria-controls={`segmented-tabpanel-${tab.id}`}
          classes={{ root: classes.tab }}
        />
      ))}
    </Tabs>
  );
};

SegmentedTabs.propTypes = {
  /** Current active tab ID (controlled by parent) */
  value: PropTypes.string.isRequired,
  /** Callback when tab changes: (tabId, event) => void */
  onChange: PropTypes.func.isRequired,
  /** Array of tab objects - id is required, order defines display order */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  /** Optional additional class name */
  className: PropTypes.string
};

SegmentedTabs.defaultProps = {
  className: ''
};

export default SegmentedTabs;
